import Stack from "@mui/material/Stack";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Typography from "@mui/material/Typography";
import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import {
  api,
  type DataProviderCounts,
  type DataProviderTotals,
  type DataProviderUsageRow,
} from "../../../api/client.ts";
import { keys } from "../../../api/queryKeys.ts";
import CenteredSpinner from "../../CenteredSpinner.tsx";
import FilterBar from "../../filters/FilterBar.tsx";
import { currentMonth, monthFilterSpec, monthLabel } from "../../filters/monthSpec.tsx";
import type { FilterSpec } from "../../filters/specs.ts";
import { useFilters, useLocalFilterState } from "../../filters/useFilters.ts";
import Panel from "../../Panel.tsx";
import EmptyTableRow from "../../table/EmptyTableRow.tsx";
import ScrollTable from "../../table/ScrollTable.tsx";
import { formatMoney, formatNumber } from "../../../utils/format.ts";

// The counters, in column order. Searches and lookups are calls made, found or
// not; the rest are data points delivered.
const COLUMNS: { key: keyof DataProviderCounts; label: string; hint: string }[] = [
  { key: "searches", label: "Searches", hint: "People searches agents made" },
  { key: "purchases", label: "Purchases", hint: "People purchases, by hand or by an agent" },
  { key: "people", label: "People", hint: "Person profiles delivered" },
  { key: "work_emails", label: "Work emails", hint: "Work emails delivered" },
  { key: "phones", label: "Phones", hint: "Mobile numbers delivered" },
  { key: "lookups", label: "Lookups", hint: "Company lookups made" },
];

/**
 * What each contact-data provider (GetLeads, Clay) did for each tenant in a
 * month: searches and lookups made, data points delivered, and what was billed.
 * A partner provider (GetLeads) is free, so its spend reads "Included".
 */
export default function DataProviderUsagePanel() {
  const usage = useUsage();
  const providers = useMemo(() => usage.data?.providers ?? [], [usage.data]);
  const specs = useMemo<FilterSpec[]>(
    () => [
      monthFilterSpec(),
      {
        kind: "options",
        key: "provider",
        label: "Provider",
        param: "provider",
        multiple: false,
        pinned: true,
        options: providers.map((p) => ({ value: p.provider, label: p.provider_label })),
      },
    ],
    [providers],
  );
  const filters = useFilters(specs, { state: usage.filterState });
  const provider = filters.get("provider");
  const rows = (usage.data?.rows ?? []).filter((r) => !provider || r.provider === provider);
  const shown = providers.filter((p) => !provider || p.provider === provider);

  return (
    <Panel
      title="Data provider usage"
      subtitle={shown.length ? <ProviderSummary providers={shown} /> : "Contact data bought and searched, per client"}
      disablePadding
    >
      <FilterBar filters={filters} count={`${formatNumber(new Set(rows.map((r) => r.tenant_id)).size)} clients`} />
      {usage.isLoading ? (
        <CenteredSpinner />
      ) : (
        <ScrollTable>
          <TableHead>
            <TableRow>
              <TableCell>Client</TableCell>
              <TableCell>Provider</TableCell>
              {COLUMNS.map((c) => (
                <TableCell key={c.key} align="right" title={c.hint}>
                  {c.label}
                </TableCell>
              ))}
              <TableCell align="right">Spend</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((r) => (
              <UsageRow key={`${r.organization_id}-${r.tenant_id}-${r.provider}`} row={r} />
            ))}
            {rows.length > 0 && <TotalRow rows={rows} />}
            {rows.length === 0 && (
              <EmptyTableRow colSpan={COLUMNS.length + 3}>
                No contact data used in {monthLabel(usage.month)}
              </EmptyTableRow>
            )}
          </TableBody>
        </ScrollTable>
      )}
    </Panel>
  );
}

// The month lives in the panel's own filter state; the query follows it.
function useUsage() {
  const filterState = useLocalFilterState();
  const month = filterState.searchParams.get("month") ?? currentMonth();
  const query = useQuery({
    queryKey: keys.platform.dataProviderUsage(month),
    queryFn: () => api.getDataProviderUsage(month),
    meta: { live: true },
  });
  return { ...query, month, filterState };
}

function spend(billed: boolean, amount: number) {
  return billed ? (
    formatMoney(amount, "USD")
  ) : (
    <Typography component="span" variant="body2" sx={{ color: "text.secondary" }}>
      Included
    </Typography>
  );
}

function UsageRow({ row }: { row: DataProviderUsageRow }) {
  return (
    <TableRow hover>
      <TableCell>
        <Typography variant="body2">{row.tenant_name}</Typography>
        <Typography variant="caption" sx={{ color: "text.secondary" }}>
          {row.organization_name}
        </Typography>
      </TableCell>
      <TableCell>{row.provider_label}</TableCell>
      {COLUMNS.map((c) => (
        <TableCell key={c.key} align="right">
          {formatNumber(row[c.key])}
        </TableCell>
      ))}
      <TableCell align="right">{spend(row.billed, row.spend)}</TableCell>
    </TableRow>
  );
}

function TotalRow({ rows }: { rows: DataProviderUsageRow[] }) {
  const sum = (key: keyof DataProviderCounts) => rows.reduce((n, r) => n + r[key], 0);
  const billed = rows.some((r) => r.billed);
  return (
    <TableRow>
      <TableCell colSpan={2} sx={{ fontWeight: 700 }}>
        Total
      </TableCell>
      {COLUMNS.map((c) => (
        <TableCell key={c.key} align="right" sx={{ fontWeight: 700 }}>
          {formatNumber(sum(c.key))}
        </TableCell>
      ))}
      <TableCell align="right" sx={{ fontWeight: 700 }}>
        {spend(billed, sum("spend"))}
      </TableCell>
    </TableRow>
  );
}

/** "GetLeads · free · 1,240 people for 12 clients", one line per provider. */
function ProviderSummary({ providers }: { providers: DataProviderTotals[] }) {
  return (
    <Stack component="span" direction="row" spacing={2} useFlexGap sx={{ flexWrap: "wrap" }}>
      {providers.map((p) => (
        <span key={p.provider}>
          {p.provider_label} · {p.billed ? formatMoney(p.spend, "USD") : "free"} ·{" "}
          {formatNumber(p.people)} people for {formatNumber(p.tenants)}{" "}
          {p.tenants === 1 ? "client" : "clients"}
        </span>
      ))}
    </Stack>
  );
}
